import {
  DatePicker,
  Drawer,
  Field,
  Input,
  RadioGroup,
  Table,
  TextArea,
} from '@/components/commons';
import { useAuth } from '@/providers/Auth';
import { ENDPOINT } from '@/services/endpoint';
import { KpiInitiativeService, KpiService } from '@/services/kpi';
import { IResponseError } from '@/types/common';
import { ISaveKpiInitiativeRequest } from '@/types/request';
import { IKpiInitiative, KpiInitiativeCategory } from '@/types/response';
import {
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
} from '@/utils';
import { ADMIN_ONLY, hasAnyPermission } from '@/utils/permission';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ConfigProvider,
  Segmented,
  Space,
  Spin,
  Tag,
  TableColumnsType,
  Typography,
} from 'antd';
import moment, { Moment } from 'moment';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { SURFACE_THEME } from '../Dashboard';
import {
  SERIES_COLORS,
  formatNumber,
  isolatedDot,
} from '../Dashboard/KpiChart';
import {
  IBucket,
  IMetric,
  bucketStartOf,
  toBuckets,
} from '../Dashboard/kpiSeries';
import { formSchema } from './formSchema';
import { initiativeImpact } from './initiativeImpact';

const { Title, Text } = Typography;

interface IProps {
  title?: string;
}

type Range = '90' | '180' | 'all';
type Granularity = 'day' | 'week';

/** 全期間の下限（ダッシュボードと同じ） */
const ALL_FROM = '2026-01-01';

const RANGE_OPTIONS = [
  { label: '直近90日', value: '90' },
  { label: '直近180日', value: '180' },
  { label: '全期間', value: 'all' },
];

const GRANULARITY_OPTIONS = [
  { label: '日次', value: 'day' },
  { label: '週次', value: 'week' },
];

/** 種類の色。線（複数人家族数）は系列の先頭色なので、印は残りの2色とグレー */
const CATEGORY: Record<
  KpiInitiativeCategory,
  { label: string; color: string }
> = {
  server: { label: 'サーバー', color: SERIES_COLORS[1] },
  app: { label: 'アプリ', color: SERIES_COLORS[2] },
  other: { label: 'その他', color: '#8c8c8c' },
};

const CATEGORY_KEYS = Object.keys(CATEGORY) as KpiInitiativeCategory[];

const CATEGORY_OPTIONS = CATEGORY_KEYS.map((value) => ({
  label: CATEGORY[value].label,
  value,
}));

const METRICS: IMetric[] = [
  {
    id: 'multiMemberFamilies',
    label: '複数人家族数',
    get: (r) => r.daily.multiMemberFamilies,
    agg: 'last',
  },
];

const INK_MUTED = '#666666';
const GRID = '#E5E5E5';

interface IFormValues {
  date?: Moment;
  category?: KpiInitiativeCategory;
  title?: string;
  description?: string | null;
}

const signed = (v: number | null) =>
  v === null ? '—' : `${v > 0 ? '+' : ''}${v.toLocaleString('ja-JP')}`;

const ChartTooltip: React.FC<
  TooltipProps<number, string> & {
    byStart: Map<string, (IKpiInitiative & { no: number })[]>;
  }
> = ({ active, payload, byStart }) => {
  if (!active || !payload?.length) return null;
  const bucket = payload[0].payload as IBucket;
  const items = byStart.get(bucket.start) ?? [];

  return (
    <div className='bg-white border border-line-subtle rounded-md px-md py-sm shadow-sm text-[12px] max-w-[320px]'>
      <div className='text-fg-muted mb-xs'>
        {bucket.from === bucket.to
          ? bucket.from
          : `${bucket.from} 〜 ${bucket.to}`}
      </div>
      <div className='flex items-center gap-sm'>
        <span className='text-fg-muted'>複数人家族数</span>
        <span className='ml-auto pl-md text-fg font-semibold'>
          {formatNumber(bucket.values.multiMemberFamilies)}
        </span>
      </div>
      {items.map((i) => (
        <div key={i.id} className='flex gap-sm mt-xs'>
          <span
            className='font-semibold'
            style={{ color: CATEGORY[i.category].color }}
          >
            {i.no}
          </span>
          <span className='text-fg'>{i.title}</span>
        </div>
      ))}
    </div>
  );
};

export const KpiInitiatives: React.FC<IProps> = ({ title }) => {
  const { currentUser } = useAuth();
  const canEdit = hasAnyPermission(currentUser, ADMIN_ONLY);

  const [range, setRange] = useState<Range>('all');
  const [granularity, setGranularity] = useState<Granularity>('week');
  const [categories, setCategories] =
    useState<KpiInitiativeCategory[]>(CATEGORY_KEYS);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editing, setEditing] = useState<IKpiInitiative | null>(null);

  const from =
    range === 'all'
      ? ALL_FROM
      : moment()
          .utcOffset(9 * 60)
          .subtract(Number(range), 'days')
          .format('YYYY-MM-DD');

  // 前後7日の純増は表示期間の外の施策にも出したいので、取得は常に全期間にして
  // グラフだけ表示期間で絞る。to を省略するとサーバーが最新の行の日付まで返す
  const kpiQuery = useQuery(['kpi-list', ALL_FROM], () =>
    KpiService.list({ from: ALL_FROM }),
  );
  const initiativesQuery = useQuery([ENDPOINT.KPI_INITIATIVE.GET_LIST], () =>
    KpiInitiativeService.getList(),
  );

  const rows = useMemo(() => kpiQuery.data?.data ?? [], [kpiQuery.data]);
  const buckets = useMemo(() => {
    // 取り込み行は複数人家族数が無い期間（〜2026-08）があるので、値のある最初の日から描く
    const firstWithValue = rows.find(
      (r) => typeof r.daily.multiMemberFamilies === 'number',
    )?.targetDate;
    const start =
      firstWithValue && firstWithValue > from ? firstWithValue : from;

    return toBuckets(
      rows.filter((r) => r.targetDate >= start),
      METRICS,
      granularity,
    );
  }, [rows, from, granularity]);

  // 番号は日付の古い順に振り、グラフの印と一覧で同じ番号を使う
  const initiatives = useMemo(
    () =>
      (initiativesQuery.data?.data ?? [])
        .map((i, idx) => ({ ...i, no: idx + 1 }))
        .filter((i) => categories.includes(i.category)),
    [initiativesQuery.data, categories],
  );

  // 軸ラベル（MM/DD）は年をまたぐと重なるので、期間の初日（年つき）で対応付ける
  const byStart = useMemo(() => {
    const map = new Map<string, (IKpiInitiative & { no: number })[]>();
    initiatives.forEach((i) => {
      const start = bucketStartOf(i.date, granularity);
      map.set(start, [...(map.get(start) ?? []), i]);
    });

    return map;
  }, [initiatives, granularity]);

  const labelByStart = useMemo(
    () => new Map(buckets.map((b) => [b.start, b.label])),
    [buckets],
  );

  const methods = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver(formSchema) as never,
  });
  const { handleSubmit, reset, setError } = methods;

  const closeDrawer = () => {
    setOpenDrawer(false);
    setEditing(null);
    reset({});
  };

  const onSaved = (message: string) => () => {
    notificationMessage({ message, type: NOTIFY_STATUS.SUCCESS });
    initiativesQuery.refetch();
    closeDrawer();
  };
  const onError = (error: IResponseError) => handleErrorCommon(error, setError);

  const createMutation = useMutation(
    (body: ISaveKpiInitiativeRequest) => KpiInitiativeService.create(body),
    { onSuccess: onSaved('施策を登録しました。'), onError },
  );
  const updateMutation = useMutation(
    ({ id, body }: { id: number; body: ISaveKpiInitiativeRequest }) =>
      KpiInitiativeService.update(id, body),
    { onSuccess: onSaved('施策を更新しました。'), onError },
  );
  const deleteMutation = useMutation(
    (id: number) => KpiInitiativeService.delete(id),
    {
      onSuccess: onSaved('施策を削除しました。'),
      onError,
    },
  );

  const openCreate = () => {
    reset({ date: moment(), category: 'server', title: '', description: '' });
    setEditing(null);
    setOpenDrawer(true);
  };

  const openEdit = (i: IKpiInitiative) => {
    reset({
      date: moment(i.date, 'YYYY-MM-DD'),
      category: i.category,
      title: i.title,
      description: i.description ?? '',
    });
    setEditing(i);
    setOpenDrawer(true);
  };

  const onSubmit = (values: IFormValues) => {
    const body: ISaveKpiInitiativeRequest = {
      date: moment(values.date).format('YYYY-MM-DD'),
      category: values.category as KpiInitiativeCategory,
      title: (values.title ?? '').trim(),
      description: values.description?.trim() || null,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, body });
    } else {
      createMutation.mutate(body);
    }
  };

  const handleDelete = () => {
    if (!editing) return;
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      maskClosable: false,
      onOk: () => deleteMutation.mutate(editing.id),
      okText: '削除する',
      cancelText: 'キャンセル',
      wrapClassName: 'delete-modal-wrapper',
    });
  };

  const columns: TableColumnsType<IKpiInitiative & { no: number }> = [
    { title: 'No.', dataIndex: 'no', width: 56, align: 'center' },
    {
      title: '日付',
      dataIndex: 'date',
      width: 110,
      render: (v: string) => moment(v, 'YYYY-MM-DD').format('YYYY/MM/DD'),
    },
    {
      title: '種類',
      dataIndex: 'category',
      width: 90,
      render: (v: KpiInitiativeCategory) => (
        <Tag color={CATEGORY[v].color}>{CATEGORY[v].label}</Tag>
      ),
    },
    { title: 'タイトル', dataIndex: 'title', width: 220 },
    {
      title: '概要',
      dataIndex: 'description',
      render: (v: string | null) => (
        <span className='whitespace-pre-wrap'>{v}</span>
      ),
    },
    {
      title: '前7日の純増',
      key: 'before',
      width: 110,
      align: 'right',
      render: (_: unknown, i) => signed(initiativeImpact(rows, i.date).before),
    },
    {
      title: '後7日の純増',
      key: 'after',
      width: 110,
      align: 'right',
      render: (_: unknown, i) => signed(initiativeImpact(rows, i.date).after),
    },
  ];

  const isLoading = kpiQuery.isLoading || initiativesQuery.isLoading;
  const isError = kpiQuery.isError || initiativesQuery.isError;

  return (
    <ConfigProvider theme={SURFACE_THEME}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className='flex flex-col gap-xl pb-xl'>
        <div className='flex flex-wrap items-end justify-between gap-md'>
          <div>
            <Title level={4} className='!mb-xs'>
              施策タイムライン
            </Title>
            <Text type='secondary' className='text-[12px]'>
              複数人家族数の推移に、本番リリースなどの施策を重ねて表示します
            </Text>
          </div>
          <Space wrap>
            <Checkbox.Group
              options={CATEGORY_OPTIONS}
              value={categories}
              onChange={(v) => setCategories(v as KpiInitiativeCategory[])}
            />
            <Segmented
              options={RANGE_OPTIONS}
              value={range}
              onChange={(v) => setRange(v as Range)}
            />
            <Segmented
              options={GRANULARITY_OPTIONS}
              value={granularity}
              onChange={(v) => setGranularity(v as Granularity)}
            />
          </Space>
        </div>

        {isError && (
          <Alert
            type='error'
            showIcon
            message='取得できませんでした'
            description={
              <Button
                size='small'
                onClick={() => {
                  kpiQuery.refetch();
                  initiativesQuery.refetch();
                }}
              >
                再読み込み
              </Button>
            }
          />
        )}

        {isLoading && (
          <div className='flex justify-center py-3xl'>
            <Spin />
          </div>
        )}

        {kpiQuery.isSuccess && initiativesQuery.isSuccess && (
          <>
            <Card
              size='small'
              title={
                <div className='py-xs'>
                  <div className='text-[14px]'>複数人家族数と施策</div>
                  <Text
                    type='secondary'
                    className='text-[12px] font-normal whitespace-normal'
                  >
                    メンバー2人以上の家族
                    {granularity === 'week' ? '（週の最終日の値）' : ''}
                    。縦線の番号は下の一覧の No.、線の色は種類
                  </Text>
                </div>
              }
              extra={
                <Space size='middle' className='text-[12px]'>
                  {CATEGORY_KEYS.map((c) => (
                    <span key={c} className='flex items-center gap-[4px]'>
                      <span
                        className='inline-block w-[10px] h-[2px]'
                        style={{ background: CATEGORY[c].color }}
                      />
                      {CATEGORY[c].label}
                    </span>
                  ))}
                </Space>
              }
            >
              <div style={{ height: 320 }}>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart
                    data={buckets.map((b) => ({ ...b, ...b.values }))}
                    margin={{ top: 24, right: 16, bottom: 0, left: 0 }}
                  >
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey='start'
                      tickFormatter={(v: string) => labelByStart.get(v) ?? v}
                      tick={{ fill: INK_MUTED, fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: GRID }}
                      minTickGap={16}
                    />
                    <YAxis
                      tick={{ fill: INK_MUTED, fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={56}
                      tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
                      allowDecimals={false}
                    />
                    <Tooltip
                      content={<ChartTooltip byStart={byStart} />}
                      cursor={{ stroke: INK_MUTED, strokeDasharray: '3 3' }}
                    />
                    {buckets
                      .filter((b) => byStart.has(b.start))
                      .map((b) => {
                        const items = byStart.get(b.start)!;
                        // 同じ期間に種類の違う施策があるときはグレーにする
                        const color = items.every(
                          (i) => i.category === items[0].category,
                        )
                          ? CATEGORY[items[0].category].color
                          : CATEGORY.other.color;

                        return (
                          <ReferenceLine
                            key={b.start}
                            x={b.start}
                            stroke={color}
                            strokeDasharray='4 3'
                            label={{
                              value: items.map((i) => i.no).join(','),
                              position: 'top',
                              fill: color,
                              fontSize: 11,
                            }}
                          />
                        );
                      })}
                    <Line
                      type='monotone'
                      dataKey='multiMemberFamilies'
                      name='複数人家族数'
                      stroke={SERIES_COLORS[0]}
                      strokeWidth={2}
                      dot={isolatedDot(
                        buckets,
                        'multiMemberFamilies',
                        SERIES_COLORS[0],
                      )}
                      activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card
              size='small'
              title={
                <div className='py-xs'>
                  <div className='text-[14px]'>施策一覧（新しい順）</div>
                  <Text
                    type='secondary'
                    className='text-[12px] font-normal whitespace-normal'
                  >
                    純増は施策の日の前日を境にした前後7日の複数人家族の増減（集計が無い日を挟むと
                    —）。 施策以外の要因も含むので目安として見る
                  </Text>
                </div>
              }
              extra={
                canEdit && (
                  <Button type='primary' onClick={openCreate}>
                    施策を登録
                  </Button>
                )
              }
            >
              <Table
                size='small'
                rowKey='id'
                pagination={false}
                scroll={{ x: 900 }}
                columns={columns}
                dataSource={[...initiatives].reverse()}
                locale={{ emptyText: '施策はまだ登録されていません' }}
                onRow={(record) => ({
                  onClick: canEdit ? () => openEdit(record) : undefined,
                  className: canEdit ? 'cursor-pointer' : undefined,
                })}
              />
            </Card>
          </>
        )}
      </div>

      {canEdit && (
        <Drawer
          title={editing ? '施策の編集' : '施策の登録'}
          open={openDrawer}
          onOk={handleSubmit(onSubmit)}
          onCancel={closeDrawer}
          onDelete={handleDelete}
          isDeleted={!!editing}
          okText={editing ? '更新' : '保存'}
          maskClosable={false}
          loading={
            createMutation.isLoading ||
            updateMutation.isLoading ||
            deleteMutation.isLoading
          }
        >
          <FormProvider {...methods}>
            <Field name='date' label='日付' required>
              <DatePicker format='YYYY/MM/DD' className='w-full' />
            </Field>
            <p className='text-xs text-gray-500 mt-[-12px] mb-3'>
              施策が効き始めた日（本番デプロイ日・ストア公開日など）
            </p>
            <Field name='category' label='種類' required>
              <RadioGroup options={CATEGORY_OPTIONS} />
            </Field>
            <Field name='title' label='タイトル' required>
              <Input
                placeholder='例: 1人家族への招待促進 nudge'
                maxLength={100}
              />
            </Field>
            <Field name='description' label='概要'>
              <TextArea
                placeholder='何を変えたか・出典（PR 番号やバージョン）など'
                rows={5}
                maxLength={2000}
              />
            </Field>
          </FormProvider>
        </Drawer>
      )}
    </ConfigProvider>
  );
};
